import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import { PieChart, Pie, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { questionnaireService } from '../services/questionnaireService';
import { questionService } from '../services/questionService';
import {
  indicatorService,
  CreateIndicatorRequest,
} from '../services/indicatorService';
import {
  reportService,
  ReportTemplateResponse,
  AvailableIndicatorResponse,
  ReportPlaceholderDto,
  ReportPreviewResponse,
  PlaceholderValueDto,
  ChartDataItem,
  SchoolStack,
  WarningRow,
  ScopeSchool,
  ScopeClass,
  TemplateScope,
} from '../services/reportService';
import { Questionnaire, Question } from '../types';
import { useToast } from '../context/ToastContext';
import ReportRichTextEditor from '../components/ReportRichTextEditor';
import { IndicatorFormModal } from '../components/IndicatorFormModal';
import { HelpButton } from '../components/HelpDialog';
import { HELP_REPORT } from '../components/HelpContent';

/**
 * 数据字段的中文映射
 */
const DATA_FIELD_LABELS: Record<string, string> = {
  VALUE: '值',
  AVG_SCORE: '平均分',
  MIN_SCORE: '最低分',
  MAX_SCORE: '最高分',
  RESPONDENT_COUNT: '参与人数',
  CONCLUSION_DISTRIBUTION: '结论分布',
  CONCLUSION_COUNT: '人数',
  CONCLUSION_PERCENTAGE: '百分比',
  CONCLUSION_PIE_CHART: '结论饼图',
  CONCLUSION_BAR_CHART: '结论柱状图',
  BY_SCHOOL_AVG: '分学校对比图(平均分)',
  BY_SCHOOL_RATE: '分学校对比图(发生率)',
  BY_SCHOOL_STACK: '分学校堆叠图(各档占比)',
};

/** 基础文本类数据字段（不含结论相关） */
const BASE_DATA_FIELDS = ['AVG_SCORE', 'MIN_SCORE', 'MAX_SCORE', 'RESPONDENT_COUNT'];
/** 图表类字段 */
const CHART_FIELDS = ['CONCLUSION_PIE_CHART', 'CONCLUSION_BAR_CHART', 'BY_SCHOOL_AVG', 'BY_SCHOOL_RATE', 'BY_SCHOOL_STACK'];

/**
 * 渲染饼图的独立组件
 */
const ReportPieChart: React.FC<{ data: ChartDataItem[] }> = ({ data }) => (
  <ResponsiveContainer width="100%" height={280}>
    <PieChart>
      <Pie
        data={data}
        cx="50%"
        cy="50%"
        outerRadius={90}
        dataKey="count"
        nameKey="name"
        label={(props: any) => `${props.name} ${props.payload?.percentage ?? ''}%`}
      >
        {data.map((entry, index) => (
          <Cell key={index} fill={entry.color || `hsl(${index * 60}, 65%, 55%)`} />
        ))}
      </Pie>
      <Tooltip formatter={(value: any, name: any) => [`${value}人`, name]} />
      <Legend />
    </PieChart>
  </ResponsiveContainer>
);

/**
 * 渲染柱状图的独立组件
 */
const ReportBarChart: React.FC<{ data: ChartDataItem[] }> = ({ data }) => (
  <ResponsiveContainer width="100%" height={280}>
    <BarChart data={data}>
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="name" />
      <YAxis />
      <Tooltip formatter={(value: any) => [`${value}人`, '人数']} />
      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
        {data.map((entry, index) => (
          <Cell key={index} fill={entry.color || `hsl(${index * 60}, 65%, 55%)`} />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
);

/** 预警名单表：逐学生一行，各分项预警分 + 预警指数（后端已筛≥3、按触发数降序）。 */
const WarningListTable: React.FC<{ data: WarningRow[] }> = ({ data }) => {
  if (!data || data.length === 0) {
    return <p style={{ textAlign: 'center', color: '#64748b', padding: '24px' }}>本校暂无达到预警标准（预警指数≥3）的学生。</p>;
  }
  const cell = (v: number) => (
    <td style={{ border: '1px solid #cbd5e1', padding: '4px 8px', textAlign: 'center',
      color: v === 3 ? '#b91c1c' : v === 1 ? '#c2410c' : '#94a3b8', fontWeight: v > 0 ? 700 : 400 }}>{v}</td>
  );
  const th = { border: '1px solid #cbd5e1', padding: '6px 8px', background: '#eaf3f4', fontWeight: 600 } as const;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: 13, margin: '8px auto', minWidth: 640 }}>
        <thead><tr>
          <th style={th}>唯一编码</th>
          <th style={th}>抑郁</th><th style={th}>焦虑</th><th style={th}>自伤风险</th>
          <th style={th}>受欺凌</th><th style={th}>学业适应风险</th><th style={th}>学校环境风险</th>
          <th style={{ ...th, background: '#dbeef0' }}>预警指数</th>
        </tr></thead>
        <tbody>
          {data.map((r, i) => (
            <tr key={i}>
              {/* 唯一编码：学生档案 name 字段存的即编码；缺失回退 code(学号/A+答卷id) */}
              <td style={{ border: '1px solid #cbd5e1', padding: '4px 8px', textAlign: 'center', fontWeight: 600 }}>{r.name && r.name !== '—' ? r.name : r.code}</td>
              {cell(r.depression)}{cell(r.anxiety)}{cell(r.selfHarm)}
              {cell(r.bullying)}{cell(r.academic)}{cell(r.environment)}
              <td style={{ border: '1px solid #cbd5e1', padding: '4px 8px', textAlign: 'center', fontWeight: 700, background: '#f8fafc' }}>{r.warningIndex}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ textAlign: 'center', color: '#64748b', fontSize: 12, marginTop: 4 }}>
        共 {data.length} 名学生进入预警（预警指数≥3，按预警指数从大到小排序）。
      </p>
    </div>
  );
};

/**
 * 横轴学校名 tag 标签：水平排列的小圆角标签，本校高亮主色。
 */
const SchoolTagTick: React.FC<any> = ({ x, y, payload, selfMap }) => {
  const name: string = payload?.value ?? '';
  const self: boolean = !!selfMap?.[name];
  const w = Math.max(36, name.length * 13 + 12);
  return (
    <g transform={`translate(${x},${y + 10})`}>
      <rect x={-w / 2} y={0} width={w} height={20} rx={10}
        fill={self ? '#4DA1A9' : '#EAF3F4'} stroke={self ? '#2E7A93' : '#D3E5E7'} strokeWidth={1} />
      <text x={0} y={14} textAnchor="middle" fontSize={11}
        fill={self ? '#ffffff' : '#456'} fontWeight={self ? 700 : 500}>{name}</text>
    </g>
  );
};

/**
 * 分学校对比柱状图：横轴=学校简称(tag)，每校一柱。
 *  - 平均分图：柱高=value(人均分)；发生率图：柱高=percentage(%)。
 *  - 校级报告：后端已把非本校名脱敏成"学校N"，本校 self=true 高亮(深色)。
 */
const ReportBySchoolBarChart: React.FC<{ data: ChartDataItem[] }> = ({ data }) => {
  // 判断是平均分还是发生率：有 value 用 value(平均分)，否则用 percentage(发生率)
  const isAvg = data.some(d => d.value != null);
  const dataKey = isAvg ? 'value' : 'percentage';
  const unit = isAvg ? '分' : '%';
  const hasSelf = data.some(d => d.self);
  const rows = data.map(d => ({ ...d, tag: d.shortName || d.name, full: d.name }));
  const selfMap: Record<string, boolean> = {};
  rows.forEach(r => { selfMap[r.tag] = !!r.self; });
  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={rows} margin={{ bottom: 40, top: 8 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="tag" interval={0} height={44} tickLine={false} axisLine={{ stroke: '#D3E5E7' }}
          tick={<SchoolTagTick selfMap={selfMap} />} />
        <YAxis tick={{ fontSize: 11 }} width={44} />
        <Tooltip
          formatter={(value: any) => [`${value}${unit}`, isAvg ? '平均分' : '发生率']}
          labelFormatter={(_l: any, p: any) => p?.[0]?.payload?.full || _l} />
        <Bar dataKey={dataKey} radius={[6, 6, 0, 0]} maxBarSize={48}>
          {rows.map((entry, index) => (
            // 系统主色系(品牌色 #4DA1A9)：校级高亮本校(主色)，其他校淡一档；市级统一主色
            <Cell key={index} fill={hasSelf ? (entry.self ? '#4DA1A9' : '#BFDCDF') : '#4DA1A9'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * 堆叠图自定义图例：按档位顺序(轻→重)横向等距排列、整体居中对齐。
 * 替代 Recharts 默认 Legend（默认按 Bar 声明顺序、item 间距不固定，导出后看着没拉齐）。
 */
const TierLegend: React.FC<{ labels: string[]; colors: Record<string, string> }> = ({ labels, colors }) => (
  <div style={{
    display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center',
    gap: '8px 24px', padding: '8px 4px 0',
  }}>
    {labels.map((label) => (
      <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', whiteSpace: 'nowrap' }}>
        <span style={{ width: 12, height: 12, borderRadius: 3, background: colors[label] || '#94a3b8', flex: '0 0 auto' }} />
        {label}
      </span>
    ))}
  </div>
);

/**
 * 分学校堆叠柱状图：每校一根堆叠柱，柱内各段=该指标「有问题」档位占全校比例%
 * （「无/健康」档已在后端剔除，柱高=有问题人群总占比，可跨校比高低）。
 * 横轴=学校简称(tag 样式)，Y=百分比(0-100)，图例=档位，渐变蓝(轻→重 浅→深)。校级本校柱描边高亮。
 */
const ReportBySchoolStackChart: React.FC<{ data: SchoolStack[] }> = ({ data }) => {
  // 档位按 order(规则 orderNum) 严格升序，避免某校缺档打乱顺序；升序渲染→轻/低在底、重/高在顶。
  const tierColors: Record<string, string> = {};
  const tierOrder: Record<string, number> = {};
  data.forEach((s) =>
    s.segments.forEach((seg) => {
      tierColors[seg.label] = seg.color;
      tierOrder[seg.label] = seg.order;
    }),
  );
  const tierLabels: string[] = Object.keys(tierOrder).sort((a, b) => tierOrder[a] - tierOrder[b]);

  const rows = data.map((s) => {
    const row: any = { name: s.shortName || s.school, full: s.school, total: s.total, self: s.self };
    s.segments.forEach((seg) => { row[seg.label] = seg.percentage; });
    return row;
  });
  const selfMap: Record<string, boolean> = {};
  rows.forEach((r) => { selfMap[r.name] = r.self; });
  const hasSelf = data.some((s) => s.self);

  return (
    <ResponsiveContainer width="100%" height={360}>
      {/* right 边距给"太矮段的柱右侧百分比标签"留位置 */}
      <BarChart data={rows} margin={{ bottom: 40, top: 8, right: 30 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="name" interval={0} height={44} tickLine={false} axisLine={{ stroke: '#D3E5E7' }}
          tick={<SchoolTagTick selfMap={selfMap} />} />
        <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} allowDecimals={false}
          tickFormatter={(v: number) => `${v}%`} tick={{ fontSize: 11 }} width={44} />
        <Tooltip
          formatter={(value: any, label: any) => [`${value}%`, label]}
          labelFormatter={(_l: any, p: any) => p?.[0]?.payload?.full || _l} />
        {/* 自定义图例：按档位升序(轻→重)、等距对齐居中，避免默认 Legend 顺序错乱/间距不齐 */}
        <Legend content={<TierLegend labels={tierLabels} colors={tierColors} />} />
        {tierLabels.map((label, i) => (
          <Bar
            key={label}
            dataKey={label}
            stackId="a"
            fill={tierColors[label] || `hsl(${200 + i * 12}, 45%, ${65 - i * 8}%)`}
            radius={i === tierLabels.length - 1 ? [6, 6, 0, 0] : undefined}
            maxBarSize={56}
          >
            {hasSelf &&
              rows.map((r, idx) => (
                <Cell key={idx} stroke={r.self ? '#1f2937' : 'transparent'} strokeWidth={r.self ? 1.5 : 0} />
              ))}
            {/* 段内百分比标签：每段都显示（校方只看 PDF，比例必须全可见）。
                段高够 → 居中；太矮 → 柱右侧深字，同柱多个矮段上下错开防重叠 */}
            <LabelList dataKey={label} content={(props: any) => {
              const { x, y, width, height, value, index } = props;
              const v = typeof value === 'number' ? value : Number(value);
              if (!v || width == null || height == null) return null;
              const dark = i >= Math.ceil(tierLabels.length / 2);
              if (height >= 12) {
                return (
                  <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="central"
                    fontSize={11} fontWeight={600} fill={dark ? '#ffffff' : '#334155'}>
                    {v.toFixed(1)}%
                  </text>
                );
              }
              const row = rows[index] || {};
              const pxPerPct = height / v;
              const smalls = tierLabels.filter((t) => {
                const tv = Number(row[t]);
                return tv > 0 && tv * pxPerPct < 12;
              });
              const k = smalls.indexOf(label);
              const n = smalls.length;
              const offsetY = k >= 0 && n > 1 ? ((n - 1) / 2 - k) * 12 : 0;
              return (
                <text x={x + width + 4} y={y + height / 2 + offsetY} textAnchor="start" dominantBaseline="central"
                  fontSize={10} fontWeight={600} fill="#334155">
                  {v.toFixed(1)}%
                </text>
              );
            }} />
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

const ReportConfigPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // 数据状态
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [template, setTemplate] = useState<ReportTemplateResponse | null>(null);
  const [availableIndicators, setAvailableIndicators] = useState<AvailableIndicatorResponse[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 编辑状态
  const [title, setTitle] = useState('大报告');
  const [content, setContent] = useState('');
  const [placeholders, setPlaceholders] = useState<ReportPlaceholderDto[]>([]);
  // 当前编辑的模板范围：市级 / 校级（各存一份）
  const [editScope, setEditScope] = useState<TemplateScope>('CITY');

  // 预览状态
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [previewData, setPreviewData] = useState<ReportPreviewResponse | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const chartRootsRef = useRef<Map<string, any>>(new Map());

  // 预览 scope（学校/班级筛选）
  const [scopeSchools, setScopeSchools] = useState<ScopeSchool[]>([]);
  const [scopeClasses, setScopeClasses] = useState<ScopeClass[]>([]);
  const [scopeSchoolId, setScopeSchoolId] = useState<number | null>(null);
  const [scopeClassId, setScopeClassId] = useState<string | null>(null);

  // 指标创建弹窗
  const [showIndicatorModal, setShowIndicatorModal] = useState(false);

  // 指标面板展开状态
  const [expandedIndicators, setExpandedIndicators] = useState<Set<number>>(new Set());

  // 加载数据
  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  /** 把某 scope 的模板内容填进编辑器（无模板则清空，便于新建）。 */
  const applyTemplate = (templateData: ReportTemplateResponse | null) => {
    if (templateData) {
      setTemplate(templateData);
      setTitle(templateData.title);
      setContent(templateData.content || '');
      setPlaceholders(
        templateData.placeholders.map((p) => ({
          type: p.type as any,
          indicatorId: p.indicatorId,
          placeholderKey: p.placeholderKey,
          displayName: p.displayName,
          expression: p.expression,
          systemMetric: p.systemMetric,
          dataField: p.dataField,
          conclusionRuleId: p.conclusionRuleId,
          orderNum: p.orderNum,
        }))
      );
    } else {
      setTemplate(null);
      setTitle(editScope === 'SCHOOL' ? '校级报告' : '大报告');
      setContent('');
      setPlaceholders([]);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [questionnaireData, templateData, indicatorsData, questionsData] = await Promise.all([
        questionnaireService.getQuestionnaire(Number(id)),
        reportService.getTemplate(Number(id), editScope),
        reportService.getAvailableIndicators(Number(id)),
        questionService.getQuestions(Number(id)),
      ]);
      setQuestionnaire(questionnaireData);
      setAvailableIndicators(indicatorsData);
      setQuestions(questionsData);
      applyTemplate(templateData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      showToast('加载失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  /** 切换市级/校级编辑：加载该 scope 已存模板（无则空白新建）。 */
  const handleSwitchScope = async (next: TemplateScope) => {
    if (next === editScope) return;
    setEditScope(next);
    try {
      setLoading(true);
      const t = await reportService.getTemplate(Number(id), next);
      applyTemplate(t);
    } catch (e) {
      console.error(e);
      showToast('加载模板失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 插入指标占位符（文本类）
   */
  const handleInsertIndicator = (
    indicator: AvailableIndicatorResponse,
    dataField: string,
    conclusionRuleId?: number,
    conclusionTitle?: string
  ) => {
    const isChart = CHART_FIELDS.includes(dataField);
    const fieldLabel = DATA_FIELD_LABELS[dataField] || dataField;

    let displayName: string;
    let key: string;

    if (conclusionRuleId && conclusionTitle) {
      // 特定结论级别的人数/百分比
      displayName = `${indicator.name}-${conclusionTitle}-${fieldLabel}`;
      key = `${indicator.suggestedPlaceholderKey}_CONC_${conclusionRuleId}_${dataField === 'CONCLUSION_COUNT' ? 'COUNT' : 'PERCENTAGE'}`;
    } else if (isChart) {
      displayName = `${indicator.name}-${fieldLabel}`;
      key = `${indicator.suggestedPlaceholderKey}_${dataField}`;
    } else {
      displayName = `${indicator.name}-${fieldLabel}`;
      key = `${indicator.suggestedPlaceholderKey}_${dataField}`;
    }

    // 调用编辑器的插入方法
    const insertFn = (window as any).__reportEditorInsertPlaceholder;
    if (insertFn) {
      insertFn(key, displayName, isChart);
    }

    // 添加到占位符列表（如果不存在）
    setPlaceholders((prev) => {
      if (prev.some((p) => p.placeholderKey === key)) return prev;
      return [
        ...prev,
        {
          type: indicator.category as any,
          indicatorId: indicator.indicatorId,
          placeholderKey: key,
          displayName,
          systemMetric: indicator.category === 'SYSTEM' ? indicator.suggestedPlaceholderKey.replace('SYS_', '') : undefined,
          dataField,
          conclusionRuleId,
          orderNum: prev.length,
        },
      ];
    });
  };

  /**
   * 创建新指标
   */
  const handleCreateIndicator = async (request: CreateIndicatorRequest) => {
    try {
      await indicatorService.createIndicator(Number(id), request);
      showToast('指标创建成功', 'success');
      setShowIndicatorModal(false);
      const indicatorsData = await reportService.getAvailableIndicators(Number(id));
      setAvailableIndicators(indicatorsData);
    } catch (error: any) {
      console.error('Failed to create indicator:', error);
      showToast(error.response?.data?.message || '指标创建失败', 'error');
    }
  };

  /**
   * 保存报告模板
   */
  const handleSave = async () => {
    if (!id) return;
    if (!title.trim()) {
      showToast('请输入报告标题', 'error');
      return;
    }

    try {
      setSaving(true);

      const usedKeys = new Set<string>();
      const regex = /data-placeholder-key="([^"]+)"/g;
      let match;
      while ((match = regex.exec(content)) !== null) {
        usedKeys.add(match[1]);
      }

      const usedPlaceholders = placeholders.filter((p) => usedKeys.has(p.placeholderKey));

      await reportService.saveTemplate(Number(id), {
        title,
        content,
        placeholders: usedPlaceholders,
      }, editScope);

      showToast(`${editScope === 'SCHOOL' ? '校级' : '市级'}报告模板保存成功`, 'success');
    } catch (error: any) {
      console.error('Failed to save:', error);
      const msg = error?.response?.data?.message || '保存失败';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * 基于当前问卷生成默认模板（不直接保存，仅填充编辑器）
   */
  const handleGenerateDefault = async () => {
    if (!id) return;
    if (content && content.trim().length > 0) {
      const ok = window.confirm('当前已有模板内容，生成默认模板将覆盖现有内容，是否继续？');
      if (!ok) return;
    }
    try {
      const def = await reportService.getDefaultTemplate(Number(id));
      setTitle(def.title || '大报告');
      setContent(def.content || '');
      // 把后端给出的占位符元数据合并进 placeholders（先用现有 + def 中的）
      if (def.placeholders) {
        setPlaceholders(def.placeholders.map((p) => ({
          type: p.type as any,
          indicatorId: p.indicatorId,
          placeholderKey: p.placeholderKey,
          displayName: p.displayName,
          expression: p.expression,
          systemMetric: p.systemMetric,
          dataField: p.dataField,
          conclusionRuleId: p.conclusionRuleId,
          orderNum: p.orderNum,
        })));
      }
      showToast('默认模板已生成，可继续编辑', 'success');
    } catch (error: any) {
      console.error(error);
      const msg = error?.response?.data?.message || '生成默认模板失败';
      showToast(msg, 'error');
    }
  };

  /**
   * 前端替换占位符并渲染图表
   */
  const renderPreview = useCallback((data: ReportPreviewResponse) => {
    if (!previewRef.current) return;

    // 清理旧的 chart roots
    chartRootsRef.current.forEach((root) => {
      try { root.unmount(); } catch {}
    });
    chartRootsRef.current.clear();

    // 构建占位符值映射
    const valueMap = new Map<string, PlaceholderValueDto>();
    data.placeholderValues.forEach((pv) => valueMap.set(pv.key, pv));

    // 第一遍：把 {{key}} 文本标记替换成 <span data-placeholder-key>，
    // 兼容手写/脚本生成、以及 data-key（而非 data-placeholder-key）的模板。
    // 与 ReportPreviewView 渲染口径保持一致。
    let html = data.templateHtml;
    valueMap.forEach((_pv, key) => {
      const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const re = new RegExp(`\\{\\{\\s*${escapedKey}\\s*\\}\\}`, 'g');
      html = html.replace(re, `<span data-placeholder-key="${key}"></span>`);
    });

    // 替换 HTML 中的占位符
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // 查找所有占位符 span/div
    const placeholderEls = tempDiv.querySelectorAll('[data-placeholder-key]');
    placeholderEls.forEach((el) => {
      const key = el.getAttribute('data-placeholder-key');
      if (!key) return;

      const pv = valueMap.get(key);
      if (!pv) {
        el.innerHTML = '<span style="color:#ef4444;">[未找到数据]</span>';
        return;
      }

      if (pv.placeholderType === 'TEXT') {
        // 文本替换
        const valueSpan = document.createElement('span');
        valueSpan.className = 'report-value';
        valueSpan.textContent = pv.resolvedValue || 'N/A';
        // 口径脚注：占比/平均分等数字后紧跟灰色小字说明计算口径
        if (pv.basis) {
          const wrap = document.createElement('span');
          wrap.appendChild(valueSpan);
          const note = document.createElement('span');
          note.className = 'report-basis';
          note.style.cssText = 'color:#94a3b8;font-size:0.78em;margin-left:2px;white-space:nowrap;';
          note.textContent = `（${pv.basis}）`;
          wrap.appendChild(note);
          el.replaceWith(wrap);
        } else {
          el.replaceWith(valueSpan);
        }
      } else {
        // 图表：替换为容器 div
        const chartContainer = document.createElement('div');
        chartContainer.className = 'report-chart-container';
        chartContainer.setAttribute('data-chart-key', key);
        chartContainer.setAttribute('data-chart-type', pv.placeholderType);
        chartContainer.style.cssText = 'width:100%;min-height:300px;margin:16px 0;padding:16px;background:#f8fafc;border-radius:12px;border:1px solid #e2e8f0;';
        el.replaceWith(chartContainer);
      }
    });

    previewRef.current.innerHTML = tempDiv.innerHTML;

    // 挂载 Recharts 图表
    const chartContainers = previewRef.current.querySelectorAll('.report-chart-container');
    chartContainers.forEach((container) => {
      const key = container.getAttribute('data-chart-key');
      const chartType = container.getAttribute('data-chart-type');
      if (!key) return;

      const pv = valueMap.get(key);
      if (!pv) {
        container.innerHTML = '<p style="text-align:center;color:#94a3b8;padding:40px;">暂无数据</p>';
        return;
      }

      // 预警名单表
      if (chartType === 'WARNING_LIST_TABLE') {
        const root = createRoot(container);
        chartRootsRef.current.set(key, root);
        root.render(<WarningListTable data={pv.warningData || []} />);
        return;
      }
      // 堆叠图用 stackData，其它图用 chartData
      if (chartType === 'BY_SCHOOL_STACK_CHART') {
        if (!pv.stackData || pv.stackData.length === 0) {
          container.innerHTML = '<p style="text-align:center;color:#94a3b8;padding:40px;">暂无数据</p>';
          return;
        }
        const root = createRoot(container);
        chartRootsRef.current.set(key, root);
        root.render(<ReportBySchoolStackChart data={pv.stackData} />);
        return;
      }

      if (!pv.chartData || pv.chartData.length === 0) {
        container.innerHTML = '<p style="text-align:center;color:#94a3b8;padding:40px;">暂无数据</p>';
        return;
      }

      const root = createRoot(container);
      chartRootsRef.current.set(key, root);

      if (chartType === 'PIE_CHART') {
        root.render(<ReportPieChart data={pv.chartData} />);
      } else if (chartType === 'BY_SCHOOL_BAR_CHART') {
        root.render(<ReportBySchoolBarChart data={pv.chartData} />);
      } else {
        root.render(<ReportBarChart data={pv.chartData} />);
      }
    });
  }, []);

  // 当预览数据变化时渲染
  useEffect(() => {
    if (isPreviewMode && previewData) {
      // 延迟确保 DOM 已更新
      requestAnimationFrame(() => renderPreview(previewData));
    }
  }, [isPreviewMode, previewData, renderPreview]);

  // 清理 chart roots
  useEffect(() => {
    return () => {
      chartRootsRef.current.forEach((root) => {
        try { root.unmount(); } catch {}
      });
    };
  }, []);

  /**
   * 重新拉预览（按当前 scope）。不重新保存模板。
   */
  const reloadPreview = useCallback(async (schoolId: number | null, classId: string | null) => {
    if (!id) return;
    try {
      setLoadingPreview(true);
      const data = await reportService.getPreview(Number(id), {
        schoolId: schoolId ?? undefined,
        classId: classId ?? undefined,
      });
      setPreviewData(data);
    } catch (error) {
      console.error('Failed to load preview:', error);
      showToast('预览加载失败', 'error');
    } finally {
      setLoadingPreview(false);
    }
  }, [id, showToast]);

  /**
   * 进入预览模式（先保存模板，再拉学校列表 + 全量预览）
   */
  const handlePreview = async () => {
    if (!id) return;
    await handleSave();

    try {
      setLoadingPreview(true);
      // 拉 scope 列表（与首次预览并行）
      const [data, schools] = await Promise.all([
        reportService.getPreview(Number(id)),
        reportService.getScopeSchools(Number(id)),
      ]);
      setPreviewData(data);
      setScopeSchools(schools);
      setScopeClasses([]);
      setScopeSchoolId(null);
      setScopeClassId(null);
      setIsPreviewMode(true);
    } catch (error) {
      console.error('Failed to load preview:', error);
      showToast('预览加载失败，请先保存模板', 'error');
    } finally {
      setLoadingPreview(false);
    }
  };

  /**
   * 切换学校
   */
  const handleScopeSchoolChange = async (schoolId: number | null) => {
    setScopeSchoolId(schoolId);
    setScopeClassId(null);
    if (schoolId === null) {
      setScopeClasses([]);
      reloadPreview(null, null);
      return;
    }
    try {
      const classes = await reportService.getScopeClasses(Number(id), schoolId);
      setScopeClasses(classes);
    } catch (e) {
      console.error('load classes failed', e);
    }
    reloadPreview(schoolId, null);
  };

  /**
   * 切换班级
   */
  const handleScopeClassChange = (classId: string | null) => {
    setScopeClassId(classId);
    reloadPreview(scopeSchoolId, classId);
  };

  const toggleIndicatorExpand = (indicatorId: number) => {
    setExpandedIndicators((prev) => {
      const next = new Set(prev);
      if (next.has(indicatorId)) next.delete(indicatorId);
      else next.add(indicatorId);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // 分离系统指标和问卷指标
  const systemIndicators = availableIndicators.filter((i) => i.category === 'SYSTEM');
  const questionnaireIndicators = availableIndicators.filter((i) => i.category === 'QUESTIONNAIRE_INDICATOR');

  return (
    <div className="p-8">
      {/* 头部 */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/questionnaires/${id}`)}
          className="flex items-center text-on-surface-variant hover:text-on-surface mb-4 transition-colors"
        >
          <span className="material-symbols-outlined mr-2" style={{ fontSize: '20px' }}>arrow_back</span>
          <span className="text-sm font-body">返回问卷详情</span>
        </button>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold font-headline text-on-surface">大报告</h1>
              <HelpButton title="大报告说明" content={HELP_REPORT} />
            </div>
            <p className="text-on-surface-variant mt-1 font-body text-sm">{questionnaire?.title}</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* 市级 / 校级 模板切换（各存一份；切换会加载对应模板） */}
            <div className="flex items-center rounded-xl bg-gray-100 p-0.5">
              {(['CITY', 'SCHOOL'] as TemplateScope[]).map((s) => (
                <button
                  key={s}
                  onClick={() => handleSwitchScope(s)}
                  disabled={isPreviewMode || saving}
                  className={`px-3 py-1.5 rounded-lg text-sm font-label font-medium transition-colors ${
                    editScope === s ? 'bg-white text-primary-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  } disabled:opacity-50`}
                  title={s === 'CITY' ? '市级报告（全市汇总）' : '校级报告（单校视角）'}
                >
                  {s === 'CITY' ? '市级' : '校级'}
                </button>
              ))}
            </div>
            <button
              onClick={handleGenerateDefault}
              className="px-4 py-2 rounded-xl text-sm font-label font-medium bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors"
              title="基于当前问卷的题目和指标生成默认模板"
            >
              <span className="material-symbols-outlined mr-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>auto_awesome</span>
              生成默认模板
            </button>
            <button
              onClick={() => setIsPreviewMode(false)}
              className={`px-4 py-2 rounded-xl text-sm font-label font-medium transition-colors ${
                !isPreviewMode
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <span className="material-symbols-outlined mr-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>edit</span>
              编辑
            </button>
            <button
              onClick={handlePreview}
              disabled={loadingPreview}
              className={`px-4 py-2 rounded-xl text-sm font-label font-medium transition-colors ${
                isPreviewMode
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {loadingPreview ? (
                <span className="animate-spin inline-block w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full mr-1" style={{ verticalAlign: 'middle' }}></span>
              ) : (
                <span className="material-symbols-outlined mr-1" style={{ fontSize: '16px', verticalAlign: 'middle' }}>visibility</span>
              )}
              预览
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 gradient-primary text-on-primary rounded-xl shadow-teal text-sm font-label font-medium disabled:opacity-50"
            >
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </div>
      </div>

      {/* 预览模式 */}
      {isPreviewMode && previewData && (
        <div className="bg-white rounded-3xl shadow-soft p-8 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4 pb-4 border-b">
            <h2 className="text-xl font-bold font-headline text-on-surface">{title}</h2>
            <div className="flex items-center gap-3 flex-wrap">
              {/* scope: school */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-on-surface-variant font-label">学校</span>
                <select
                  value={scopeSchoolId ?? ''}
                  onChange={(e) =>
                    handleScopeSchoolChange(e.target.value === '' ? null : Number(e.target.value))
                  }
                  disabled={loadingPreview || scopeSchools.length === 0}
                  className="h-9 px-3 bg-surface-container rounded-lg text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:opacity-50"
                >
                  <option value="">全部学校</option>
                  {scopeSchools.map((s) => (
                    <option key={s.schoolId} value={s.schoolId}>
                      {s.schoolName}
                    </option>
                  ))}
                </select>
              </div>
              {/* scope: class（仅在选了学校后启用） */}
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-on-surface-variant font-label">班级</span>
                <select
                  value={scopeClassId ?? ''}
                  onChange={(e) =>
                    handleScopeClassChange(e.target.value === '' ? null : e.target.value)
                  }
                  disabled={loadingPreview || scopeSchoolId === null || scopeClasses.length === 0}
                  className="h-9 px-3 bg-surface-container rounded-lg text-on-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 disabled:opacity-50"
                >
                  <option value="">全部班级</option>
                  {scopeClasses.map((c) => (
                    <option key={c.classId} value={c.classId}>
                      {c.className}
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-xs text-on-surface-variant font-label">
                {scopeClassId ? '班级视图' : scopeSchoolId ? '学校视图' : '全量视图'}
                {' · '}
                {previewData.placeholderValues.length} 项已解析
              </div>
            </div>
          </div>
          <div
            ref={previewRef}
            className="prose prose-sm max-w-none report-preview-content"
          />
          {/* 指标解析详情 */}
          {previewData.placeholderValues.filter(pv => pv.placeholderType === 'TEXT').length > 0 && (
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-sm font-semibold text-on-surface-variant mb-3 font-label">指标解析详情</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {previewData.placeholderValues
                  .filter(pv => pv.placeholderType === 'TEXT')
                  .map((pv) => (
                    <div key={pv.key} className="bg-blue-50 rounded-xl p-3">
                      <div className="text-xs text-blue-600 font-label">{pv.displayName}</div>
                      <div className="text-sm font-semibold text-blue-800 mt-1">{pv.resolvedValue}</div>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* 编辑模式 */}
      {!isPreviewMode && (
        <div className="flex gap-6">
          {/* 左侧：编辑器 */}
          <div className="flex-1 min-w-0">
            <div className="mb-4">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="报告标题"
                className="w-full px-4 py-3 text-lg font-semibold bg-surface-container-lowest rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-200 font-headline"
              />
            </div>

            <ReportRichTextEditor
              value={content}
              onChange={setContent}
              placeholder="在此编辑报告内容。支持从 Word 复制粘贴，可从右侧面板插入指标..."
            />

            {placeholders.length > 0 && (
              <div className="mt-4 bg-surface-container-lowest rounded-2xl p-4 shadow-soft">
                <h3 className="text-sm font-semibold text-on-surface-variant mb-2 font-label">已添加的指标 ({placeholders.length})</h3>
                <div className="flex flex-wrap gap-2">
                  {placeholders.map((p) => (
                    <span
                      key={p.placeholderKey}
                      className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-body ${
                        CHART_FIELDS.includes(p.dataField)
                          ? 'bg-purple-50 text-purple-700'
                          : 'bg-blue-50 text-blue-700'
                      }`}
                    >
                      {CHART_FIELDS.includes(p.dataField) && (
                        <span className="mr-1">{p.dataField.includes('PIE') ? '🥧' : '📊'}</span>
                      )}
                      {p.displayName}
                      <span className="ml-1 opacity-50 text-[10px]">
                        {p.type === 'SYSTEM' ? '系统' : '指标'}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* 右侧：指标面板 */}
          <div className="w-80 flex-shrink-0">
            <div className="bg-surface-container-lowest rounded-2xl shadow-soft p-5 sticky top-6">
              <h3 className="text-base font-semibold font-headline text-on-surface mb-4">
                <span className="material-symbols-outlined mr-2" style={{ fontSize: '20px', verticalAlign: 'middle' }}>analytics</span>
                指标面板
              </h3>

              {/* 系统指标 */}
              <div className="mb-5">
                <h4 className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-2 font-label">
                  系统指标
                </h4>
                <div className="space-y-1">
                  {systemIndicators.map((ind) => (
                    <button
                      key={ind.suggestedPlaceholderKey}
                      onClick={() => handleInsertIndicator(ind, 'VALUE')}
                      className="w-full text-left px-3 py-2 rounded-xl hover:bg-blue-50 text-sm font-body text-on-surface transition-colors flex items-center"
                    >
                      <span className="material-symbols-outlined mr-2 text-blue-500" style={{ fontSize: '16px' }}>
                        {ind.suggestedPlaceholderKey.includes('COUNT') ? 'group' : 'percent'}
                      </span>
                      {ind.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* 问卷指标 */}
              <div className="mb-2">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-on-surface-variant uppercase tracking-wider font-label">
                    问卷指标
                  </h4>
                  <button
                    onClick={() => setShowIndicatorModal(true)}
                    className="px-2 py-0.5 text-xs gradient-primary text-on-primary shadow-teal rounded-lg hover:opacity-90 font-label"
                  >
                    + 新建指标
                  </button>
                </div>
                {questionnaireIndicators.length > 0 ? (
                  <div className="space-y-2">
                    {questionnaireIndicators.map((ind) => {
                      const isExpanded = expandedIndicators.has(ind.indicatorId!);
                      const hasConclusions = ind.conclusions && ind.conclusions.length > 0;

                      return (
                        <div key={ind.indicatorId} className="border border-gray-100 rounded-xl p-3">
                          {/* 指标名称 + 展开按钮 */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-semibold text-on-surface font-body">{ind.name}</div>
                            {hasConclusions && (
                              <button
                                onClick={() => toggleIndicatorExpand(ind.indicatorId!)}
                                className="text-xs text-gray-400 hover:text-gray-600"
                              >
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                                  {isExpanded ? 'expand_less' : 'expand_more'}
                                </span>
                              </button>
                            )}
                          </div>

                          {/* 基础数据字段 */}
                          <div className="flex flex-wrap gap-1">
                            {BASE_DATA_FIELDS.filter(f => ind.availableDataFields.includes(f)).map((field) => (
                              <button
                                key={field}
                                onClick={() => handleInsertIndicator(ind, field)}
                                className="px-2 py-1 bg-gray-100 hover:bg-blue-100 text-xs rounded-lg text-gray-700 hover:text-blue-700 transition-colors font-label"
                              >
                                {DATA_FIELD_LABELS[field] || field}
                              </button>
                            ))}
                          </div>

                          {/* 图表按钮 */}
                          {hasConclusions && (
                            <div className="flex flex-wrap gap-1 mt-1.5">
                              {CHART_FIELDS.filter(f => ind.availableDataFields.includes(f)).map((field) => (
                                <button
                                  key={field}
                                  onClick={() => handleInsertIndicator(ind, field)}
                                  className="px-2 py-1 bg-purple-50 hover:bg-purple-100 text-xs rounded-lg text-purple-700 hover:text-purple-800 transition-colors font-label"
                                >
                                  {field.includes('PIE') ? '🥧' : '📊'} {DATA_FIELD_LABELS[field]}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* 展开的结论级别 */}
                          {isExpanded && hasConclusions && (
                            <div className="mt-2 pt-2 border-t border-gray-100">
                              <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                                结论详情
                              </div>
                              <div className="space-y-1.5">
                                {ind.conclusions!.map((conc) => (
                                  <div key={conc.ruleId} className="flex items-center justify-between">
                                    <div className="flex items-center">
                                      <span
                                        className="w-2.5 h-2.5 rounded-full mr-2 flex-shrink-0"
                                        style={{ backgroundColor: conc.color }}
                                      />
                                      <span className="text-xs text-gray-700">{conc.title}</span>
                                    </div>
                                    <div className="flex gap-1">
                                      <button
                                        onClick={() => handleInsertIndicator(ind, 'CONCLUSION_COUNT', conc.ruleId, conc.title)}
                                        className="px-1.5 py-0.5 bg-gray-100 hover:bg-blue-100 text-[10px] rounded text-gray-600 hover:text-blue-700 transition-colors"
                                      >
                                        人数
                                      </button>
                                      <button
                                        onClick={() => handleInsertIndicator(ind, 'CONCLUSION_PERCENTAGE', conc.ruleId, conc.title)}
                                        className="px-1.5 py-0.5 bg-gray-100 hover:bg-blue-100 text-[10px] rounded text-gray-600 hover:text-blue-700 transition-colors"
                                      >
                                        百分比
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-on-surface-variant font-body">
                    暂无问卷指标，点击"新建指标"创建
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 指标创建弹窗 */}
      {showIndicatorModal && (
        <IndicatorFormModal
          indicator={null}
          questions={questions}
          onClose={() => setShowIndicatorModal(false)}
          onSave={handleCreateIndicator}
        />
      )}

      <style>
        {`
          .report-preview-content .report-value {
            font-weight: bold;
            color: #0369a1;
            background: #e0f2fe;
            padding: 1px 6px;
            border-radius: 4px;
          }

          .report-preview-content table {
            border-collapse: collapse;
            width: 100%;
            margin: 8px 0;
          }

          .report-preview-content td,
          .report-preview-content th {
            border: 1px solid #d1d5db;
            padding: 8px;
          }

          .report-preview-content th {
            background-color: #f3f4f6;
            font-weight: bold;
          }
        `}
      </style>
    </div>
  );
};

export default ReportConfigPage;
